import { useEffect, useMemo, useState } from "react";
import { getPortfolioHealth } from "./StockTradeService";
import { refreshCoverageFromNse } from "./StockService";
import { getAllocationTargets, saveAllocationTargets } from "./StockAllocationService";

const fmtINR = (n) => {
  if (n == null || isNaN(Number(n))) return "₹0";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n);
};
const fmtPct = (n) => (n == null ? "—" : Number(n).toFixed(2) + "%");

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const normalizeKey = (value, fallback) => {
  const key = (value || "").toString().trim();
  return key ? key : fallback;
};

export default function StockSummary() {
  const [health, setHealth] = useState({
    totalInvested: 0,
    totalRealizedPnl: 0,
    totalUnrealizedPnl: 0,
    totalPnl: 0,
    currentValue: 0,
    sectorBuckets: [],
    capBuckets: [],
    sectorExposure: [],
    capExposure: [],
    countryExposure: [],
    topPositions: [],
    riskSignals: [],
    concentration: null,
    drawdown: null,
  });
  const [targets, setTargets] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshingCoverage, setIsRefreshingCoverage] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    const [healthData, targetData] = await Promise.all([
      getPortfolioHealth(),
      getAllocationTargets(),
    ]);
    setHealth(
      healthData || {
        totalInvested: 0,
        totalRealizedPnl: 0,
        totalUnrealizedPnl: 0,
        totalPnl: 0,
        currentValue: 0,
        sectorBuckets: [],
        capBuckets: [],
        sectorExposure: [],
        capExposure: [],
        countryExposure: [],
        topPositions: [],
        riskSignals: [],
        concentration: null,
        drawdown: null,
      }
    );
    setTargets(Array.isArray(targetData) ? targetData : []);
  };

  const sectorTargets = useMemo(() => {
    return targets.filter((t) => t.targetType === "SECTOR");
  }, [targets]);

  const capTargets = useMemo(() => {
    return targets.filter((t) => t.targetType === "CAP");
  }, [targets]);

  const sectorMap = useMemo(() => {
    const map = new Map();
    sectorTargets.forEach((t) => map.set(normalizeKey(t.targetKey, "Unknown"), toNumber(t.targetPercent)));
    return map;
  }, [sectorTargets]);

  const capMap = useMemo(() => {
    const map = new Map();
    capTargets.forEach((t) => map.set(normalizeKey(t.targetKey, "UNKNOWN"), toNumber(t.targetPercent)));
    return map;
  }, [capTargets]);

  const capLabel = (key) => {
    if (key === "LARGE_CAP") return "Large Cap";
    if (key === "MID_CAP") return "Mid Cap";
    if (key === "SMALL_CAP") return "Small Cap";
    if (key === "UNKNOWN") return "Unknown";
    return key;
  };

  const sectorRows = useMemo(() => {
    return (health.sectorBuckets || []).map((b) => ({
      key: normalizeKey(b.key, "Unknown"),
      invested: toNumber(b.investedAmount),
      percent: toNumber(b.percent),
      target: sectorMap.get(normalizeKey(b.key, "Unknown")) ?? 0,
      count: b.count ?? 0,
    }));
  }, [health, sectorMap]);

  const capRows = useMemo(() => {
    return (health.capBuckets || []).map((b) => ({
      key: normalizeKey(b.key, "UNKNOWN"),
      label: capLabel(normalizeKey(b.key, "UNKNOWN")),
      invested: toNumber(b.investedAmount),
      percent: toNumber(b.percent),
      target: capMap.get(normalizeKey(b.key, "UNKNOWN")) ?? 0,
      count: b.count ?? 0,
    }));
  }, [health, capMap]);

  const sectorExposureRows = useMemo(() => {
    return (health.sectorExposure || []).map((b) => ({
      key: normalizeKey(b.key, "Unknown"),
      invested: toNumber(b.investedAmount),
      percent: toNumber(b.percent),
      target: toNumber(b.targetPercent),
      drift: toNumber(b.driftPercent),
      count: b.count ?? 0,
    }));
  }, [health]);

  const capExposureRows = useMemo(() => {
    return (health.capExposure || []).map((b) => ({
      key: normalizeKey(b.key, "UNKNOWN"),
      label: capLabel(normalizeKey(b.key, "UNKNOWN")),
      invested: toNumber(b.investedAmount),
      percent: toNumber(b.percent),
      target: toNumber(b.targetPercent),
      drift: toNumber(b.driftPercent),
      count: b.count ?? 0,
    }));
  }, [health]);

  const countryExposureRows = useMemo(() => {
    return (health.countryExposure || []).map((b) => ({
      key: normalizeKey(b.key, "Unknown"),
      invested: toNumber(b.investedAmount),
      percent: toNumber(b.percent),
      count: b.count ?? 0,
    }));
  }, [health]);

  const buildTargetPayload = () => {
    const payload = [];
    sectorRows.forEach((row) => {
      payload.push({
        targetType: "SECTOR",
        targetKey: row.key,
        targetPercent: row.target,
      });
    });
    capRows.forEach((row) => {
      payload.push({
        targetType: "CAP",
        targetKey: row.key,
        targetPercent: row.target,
      });
    });
    return payload;
  };

  const totalTarget = (rows) => rows.reduce((acc, r) => acc + toNumber(r.target), 0);

  const shouldPromptCoverage =
    (sectorRows.length > 0 && sectorRows.every((r) => r.key.toLowerCase() === "unknown")) ||
    (countryExposureRows.length > 0 && countryExposureRows.every((r) => r.key.toLowerCase() === "unknown"));

  const refreshCoverage = async () => {
    setIsRefreshingCoverage(true);
    try {
      const result = await refreshCoverageFromNse();
      await load();
      if (result) {
        const parts = [`Updated: ${result.updated}`];
        if (Number.isFinite(result.updatedNse)) parts.push(`NSE: ${result.updatedNse}`);
        if (Number.isFinite(result.updatedBse)) parts.push(`BSE: ${result.updatedBse}`);
        window.alert(`Coverage refresh done. ${parts.join(" · ")}`);
      }
    } catch (err) {
      window.alert(err?.message || "Failed to refresh coverage");
    } finally {
      setIsRefreshingCoverage(false);
    }
  };

  const saveTargets = async () => {
    setIsSaving(true);
    try {
      await saveAllocationTargets(buildTargetPayload());
      await load();
      window.alert("Allocation targets saved.");
    } catch (err) {
      window.alert(err?.message || "Failed to save targets.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="card">
      <div className="stocks-toolbar">
        <div className="stocks-summary">
          <span className="stocks-badge">Invested: {fmtINR(health.totalInvested)}</span>
          <span className="stocks-badge">Value: {fmtINR(health.currentValue)}</span>
          <span className={`stocks-badge ${health.totalPnl >= 0 ? "stocks-good" : "stocks-bad"}`}>
            Total P&L: {fmtINR(health.totalPnl)}
          </span>
          <span className={`stocks-badge ${health.totalRealizedPnl >= 0 ? "stocks-good" : "stocks-bad"}`}>
            Realized: {fmtINR(health.totalRealizedPnl)}
          </span>
          <span className={`stocks-badge ${health.totalUnrealizedPnl >= 0 ? "stocks-good" : "stocks-bad"}`}>
            Unrealized: {fmtINR(health.totalUnrealizedPnl)}
          </span>
        </div>
        <div className="row-actions">
          <button className="btn btn-primary" onClick={saveTargets} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Targets"}
          </button>
          <button className="btn btn-secondary" onClick={refreshCoverage} disabled={isRefreshingCoverage}>
            {isRefreshingCoverage ? "Refreshing..." : "Refresh Coverage (NSE/BSE)"}
          </button>
        </div>
      </div>

      <div className="stocks-summary-grid">
        <div className="card">
          <div className="card-title">Portfolio Health</div>
          <div className="stocks-summary">
            <span className="stocks-badge">Value: {fmtINR(health.currentValue)}</span>
            <span className="stocks-badge">
              Drawdown: {fmtPct(health.drawdown?.drawdownPercent ?? 0)} / {health.drawdown?.lookbackDays ?? 0}d
            </span>
            <span className="stocks-badge">
              Top 1: {fmtPct(health.concentration?.top1Percent ?? 0)} · Top 3: {fmtPct(health.concentration?.top3Percent ?? 0)}
            </span>
            <span className="stocks-badge">Top 5: {fmtPct(health.concentration?.top5Percent ?? 0)}</span>
          </div>
          <div className="stocks-muted" style={{ marginTop: 8 }}>
            Drawdown uses latest prices vs peak over lookback window.
          </div>
        </div>

        <div className="card">
          <div className="card-title">Risk Signals</div>
          <div className="stocks-risk-list">
            {(health.riskSignals || []).map((signal) => (
              <div key={`${signal.code}-${signal.title}`} className={`stocks-risk-item stocks-risk-${(signal.severity || "low").toLowerCase()}`}>
                <div className="stocks-risk-title">{signal.title}</div>
                <div className="stocks-risk-meta">{signal.message}</div>
              </div>
            ))}
            {(health.riskSignals || []).length === 0 && (
              <div className="stocks-empty">No risk signals right now.</div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-title">Top Positions</div>
          <div className="data-table-wrap">
            <table className="stocks-table">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Qty</th>
                  <th>Invested</th>
                  <th>Current Price</th>
                  <th>Unrealized PnL</th>
                  <th>P/L %</th>
                </tr>
              </thead>
              <tbody>
                {(health.topPositions || []).map((p) => (
                  <tr key={p.stockId || p.symbol}>
                    <td><strong>{p.symbol}</strong></td>
                    <td>{p.quantity}</td>
                    <td>{fmtINR(p.investedAmount)}</td>
                    <td>{fmtINR(p.currentPrice)}</td>
                    <td className={p.unrealizedPnl >= 0 ? "stocks-good" : "stocks-bad"}>{fmtINR(p.unrealizedPnl)}</td>
                    <td className={p.profitLossPercent >= 0 ? "stocks-good" : "stocks-bad"}>{fmtPct(p.profitLossPercent)}</td>
                  </tr>
                ))}
                {(health.topPositions || []).length === 0 && (
                  <tr>
                    <td colSpan="6">No positions yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-title">Sector Allocation</div>
          <div className="stocks-target-meta">Target total: {totalTarget(sectorRows)}%</div>
          {shouldPromptCoverage && (
            <div className="stocks-muted" style={{ marginTop: 6 }}>
              Sector data is missing. Click “Refresh Coverage (NSE/BSE)” to populate sectors.
            </div>
          )}
          {(sectorRows || []).map((row) => (
            <div key={row.key} className="stocks-alloc-row">
              <div className="stocks-alloc-label">
                <div className="stocks-alloc-name">{row.key}</div>
                <div className="stocks-alloc-meta">
                  {row.count} stocks · {row.percent.toFixed(2)}% · {row.invested}
                </div>
              </div>
              <div className="stocks-alloc-bar">
                <span style={{ width: `${Math.min(row.percent, 100)}%` }} />
              </div>
              <input
                className="stocks-target-input"
                type="number"
                value={row.target}
                onChange={(e) => {
                  const value = toNumber(e.target.value);
                  setTargets((prev) => {
                    const next = prev.filter((t) => !(t.targetType === "SECTOR" && t.targetKey === row.key));
                    next.push({ targetType: "SECTOR", targetKey: row.key, targetPercent: value });
                    return next;
                  });
                }}
              />
            </div>
          ))}
          {sectorRows.length === 0 && <div className="stocks-empty">No sector data yet.</div>}
        </div>

        <div className="card">
          <div className="card-title">Market Cap Allocation</div>
          <div className="stocks-muted" style={{ marginTop: -6, marginBottom: 8 }}>
            Targets are used to calculate drift below after you click “Save Targets”.
          </div>
          <div className="stocks-target-meta">Target total: {totalTarget(capRows)}%</div>
          {(capRows || []).map((row) => (
            <div key={row.key} className="stocks-alloc-row">
              <div className="stocks-alloc-label">
                <div className="stocks-alloc-name">{row.label}</div>
                <div className="stocks-alloc-meta">
                  {row.count} stocks · {row.percent.toFixed(2)}% · {row.invested}
                </div>
              </div>
              <div className="stocks-alloc-bar">
                <span style={{ width: `${Math.min(row.percent, 100)}%` }} />
              </div>
              <input
                className="stocks-target-input"
                type="number"
                value={row.target}
                onChange={(e) => {
                  const value = toNumber(e.target.value);
                  setTargets((prev) => {
                    const next = prev.filter((t) => !(t.targetType === "CAP" && t.targetKey === row.key));
                    next.push({ targetType: "CAP", targetKey: row.key, targetPercent: value });
                    return next;
                  });
                }}
              />
            </div>
          ))}
          {capRows.length === 0 && <div className="stocks-empty">No cap data yet.</div>}
        </div>

        <div className="card">
          <div className="card-title">Sector Drift vs Targets</div>
          {(sectorExposureRows || []).map((row) => (
            <div key={row.key} className="stocks-alloc-row">
              <div className="stocks-alloc-label">
                <div className="stocks-alloc-name">{row.key}</div>
                <div className="stocks-alloc-meta">
                  {row.count} stocks · {row.percent.toFixed(2)}% · Target {row.target.toFixed(2)}%
                </div>
              </div>
              <div className="stocks-alloc-bar">
                <span style={{ width: `${Math.min(row.percent, 100)}%` }} />
              </div>
              <div className={row.drift >= 0 ? "stocks-good" : "stocks-bad"}>
                {row.drift.toFixed(2)}%
              </div>
            </div>
          ))}
          {sectorExposureRows.length === 0 && <div className="stocks-empty">No sector exposure yet.</div>}
        </div>

        <div className="card">
          <div className="card-title">Cap Drift vs Targets</div>
          {(capExposureRows || []).map((row) => (
            <div key={row.key} className="stocks-alloc-row">
              <div className="stocks-alloc-label">
                <div className="stocks-alloc-name">{row.label}</div>
                <div className="stocks-alloc-meta">
                  {row.count} stocks · {row.percent.toFixed(2)}% · Target {row.target.toFixed(2)}%
                </div>
              </div>
              <div className="stocks-alloc-bar">
                <span style={{ width: `${Math.min(row.percent, 100)}%` }} />
              </div>
              <div className={row.drift >= 0 ? "stocks-good" : "stocks-bad"}>
                {row.drift.toFixed(2)}%
              </div>
            </div>
          ))}
          {capExposureRows.length === 0 && <div className="stocks-empty">No cap exposure yet.</div>}
        </div>

        <div className="card">
          <div className="card-title">Country Exposure</div>
          {(countryExposureRows || []).map((row) => (
            <div key={row.key} className="stocks-alloc-row">
              <div className="stocks-alloc-label">
                <div className="stocks-alloc-name">{row.key}</div>
                <div className="stocks-alloc-meta">
                  {row.count} stocks · {row.percent.toFixed(2)}% · {row.invested}
                </div>
              </div>
              <div className="stocks-alloc-bar">
                <span style={{ width: `${Math.min(row.percent, 100)}%` }} />
              </div>
              <div className="stocks-muted">{row.percent.toFixed(2)}%</div>
            </div>
          ))}
          {countryExposureRows.length === 0 && <div className="stocks-empty">No country exposure yet.</div>}
        </div>
      </div>
    </div>
  );
}
